/* @flow */

import * as React from 'react';
import classnames from 'classnames';
import {colors} from '../colors';
import './card.css';

type Props = {
    children: React.Node,
    hasBorder: boolean,
    /** By default, we will show a shadow if `hasBorder` is false, but not if it is true. */
    hasShadow?: boolean,
    /** If hasBorder is `true`, optionally change the color. */
    borderColor?: string,
    /** If hasDottedBorder is `true`, display a dotted grey border. */
    hasDottedBorder?: boolean,
    /** If true, the card will size horizontally according to the content, not the parent's width */
    fitContent: boolean,
    /** If true, the card will size vertically according to the content, not the parent's height */
    fitHeight: boolean,
    /** We can optionally add a colored line along the left side */
    leftLineColor?: string,
    onClick?: () => void,
};

Card.defaultProps = {
    borderColor: colors.offWhiteDk,
    fitContent: false,
    fitHeight: false,
};

Card.displayName = 'Card';

/**
 * This is a simple UI component that gives us a standard way of creating
 * a "card" with rounded corners and an optional shadow.
 *
 * It is up to the component using the Card shell to provide all of the
 * contents via \`children\`.
 */
export function Card({
    hasBorder,
    hasShadow,
    children,
    borderColor,
    hasDottedBorder,
    fitContent,
    fitHeight,
    leftLineColor,
    onClick,
}: Props) {
    const containerStyleName = classnames({
        'card--bordered': hasBorder,
        'card--shadowed': typeof hasShadow !== 'undefined' ? hasShadow : !hasBorder,
        'fit-content': fitContent,
        'fit-height': fitHeight,
        'card--button': onClick,
    });

    const getBorderStyle = () => {
        if (hasDottedBorder) {
            return {borderStyle: 'dashed', borderColor: colors.grey};
        } else if (leftLineColor) {
            return {borderColor, borderLeft: `3px solid ${leftLineColor}`};
        } else return {borderColor};
    };

    return (
        <div styleName={containerStyleName} style={getBorderStyle()} onClick={onClick}>
            {children}
        </div>
    );
}
